// Rubrique « Paiements » du portail (lot L2) : créances à payer avec le reste et l'échéance, accès
// aux paiements, aux quittances et aux emplacements de marché de l'usager. Les routes absentes du
// serveur (lot non déployé) sont annoncées, jamais remplacées par un faux contenu.
import { EtatVide } from '@cc/ui';
import { CreditCard } from 'lucide-react';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { EnTeteCitoyen, ExigeConnexion, Ligne, Liste, Section, TexteStatut } from '../commun';
import { useMesAttributions, useMesCreances, useMesPaiements, useMesQuittances } from './api';
import { aPayer, enAttente, francs, libelleEtatCreance, libelleEtatPaiement, montant, tonEtatCreance, tonEtatPaiement, totalAPayer, trierCreances } from './logique';
import type { CreanceUsager } from './types';
import styles from '../citoyen.module.css';

export function sousTitreCreance(c: CreanceUsager): string {
  const parts = [c.numero, c.collectivite.nom];
  if (aPayer(c)) parts.push(`échéance le ${date(c.echeance)}`);
  if (c.montant_paye > 0 && aPayer(c)) parts.push(`${montant(c.montant_paye)} déjà payés`);
  if (c.fictif) parts.push('tarif fictif de présentation');
  return parts.join(' · ');
}

export function PagePaiements() {
  const creances = useMesCreances();
  const paiements = useMesPaiements();
  const quittances = useMesQuittances();
  const attributions = useMesAttributions();
  const liste = trierCreances(creances.data ?? []);
  const ouvertes = liste.filter(aPayer);
  const total = totalAPayer(liste);
  const enCours = (paiements.data ?? []).filter(enAttente);
  const recents = (paiements.data ?? []).slice(0, 3);
  return (
    <>
      <EnTeteCitoyen titre="Paiements" sousTitre={creances.data ? (ouvertes.length > 0 ? `${ouvertes.length} créance${ouvertes.length > 1 ? 's' : ''} à payer, ${francs(total)} au total.` : 'Aucune somme à payer pour l\'instant.') : 'Vos créances, vos paiements et vos quittances, dans toutes les collectivités.'} />
      <ExigeConnexion titre="Connectez-vous pour voir vos paiements">
        <Section titre="À payer" id="a-payer">
          <EtatRequete chargement={creances.isPending} erreur={creances.error} onReessayer={() => void creances.refetch()} fonction="Vos créances" lot="L2" lignes={3}>
            {ouvertes.length === 0 ? (
              <EtatVide titre="Rien à payer" icone={CreditCard}>
                <p>Les redevances d&apos;emplacement, les droits et les frais de démarche apparaîtront ici dès qu&apos;une collectivité vous les demande.</p>
              </EtatVide>
            ) : (
              <Liste libelle="Créances à payer">
                {ouvertes.map((c) => (
                  <Ligne key={c.id} href={`/citoyen/paiements/creances/${c.id}`} titre={c.libelle} sousTitre={sousTitreCreance(c)} droite={<span className={styles.mono}>{montant(c.reste)}</span>} />
                ))}
              </Liste>
            )}
          </EtatRequete>
        </Section>
        {enCours.length > 0 ? (
          <Section titre="Paiements en attente" id="en-attente">
            <Liste libelle="Paiements en attente du prestataire">
              {enCours.map((p) => (
                <Ligne key={p.id} href={`/citoyen/paiements/suivi/${p.reference_serveur}`} titre={`${montant(p.montant)} · ${p.collectivite.nom}`} sousTitre={`Référence ${p.reference_serveur}`} droite={<TexteStatut libelle={libelleEtatPaiement(p.etat)} ton={tonEtatPaiement(p.etat)} />} />
              ))}
            </Liste>
          </Section>
        ) : null}
        <Section titre="Historique" id="historique">
          <Liste libelle="Rubriques des paiements">
            <Ligne href="/citoyen/paiements/historique" titre="Mes paiements" sousTitre={paiements.data ? `${paiements.data.length} paiement${paiements.data.length > 1 ? 's' : ''}` : 'Paiements en ligne et à la caisse'} droite={recents[0] ? <TexteStatut libelle={libelleEtatPaiement(recents[0].etat)} ton={tonEtatPaiement(recents[0].etat)} /> : undefined} />
            <Ligne href="/citoyen/paiements/quittances" titre="Mes quittances" sousTitre={quittances.data ? `${quittances.data.length} quittance${quittances.data.length > 1 ? 's' : ''}, PDF téléchargeables` : 'Preuves de paiement avec code de vérification'} />
            <Ligne href="/citoyen/paiements/emplacements" titre="Mes emplacements de marché" sousTitre={attributions.data ? `${attributions.data.length} attribution${attributions.data.length > 1 ? 's' : ''}` : 'Attributions et redevances'} />
          </Liste>
        </Section>
        {liste.filter((c) => !aPayer(c)).length > 0 ? (
          <Section titre="Créances réglées ou closes" id="closes">
            <Liste libelle="Créances réglées ou closes">
              {liste
                .filter((c) => !aPayer(c))
                .slice(0, 10)
                .map((c) => (
                  <Ligne key={c.id} href={`/citoyen/paiements/creances/${c.id}`} titre={c.libelle} sousTitre={sousTitreCreance(c)} droite={<TexteStatut libelle={libelleEtatCreance(c)} ton={tonEtatCreance(c)} />} />
                ))}
            </Liste>
          </Section>
        ) : null}
      </ExigeConnexion>
    </>
  );
}
